/**
 * Inventory › Tracking contracts (قرارداد ردیابی).
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — inventory list archetype (V2DataGrid) with
 * V2Modal forms; docs/design-system/V2_USAGE.md §4 and §6.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Checkbox,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2Modal,
  V2ToastStack,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPut } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { faDigits } from '../../lib/faDigits.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import { displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import {
  FEFO_POLICY_KEYS,
  FEFO_POLICY_LABEL,
  TRACKING_MODE_KEYS,
  TRACKING_MODE_META,
  canonicalTrackingMode,
  labelOf,
  metaOf,
  optionsOf,
} from './inventoryDisplay.js';
import { useDebouncedValue, useToasts } from './useInventoryUi.js';
import './inventory.css';

interface TrackingProfileItem {
  readonly product_id: string;
  readonly product_code: string;
  readonly product_name: string;
  readonly base_uom_code: string;
  readonly base_uom_name: string;
  readonly tracking_mode: string;
  readonly profile_version: number;
  readonly precision: number;
  readonly requires_manufacture_date: boolean;
  readonly requires_expiry: boolean;
  readonly min_shelf_life_days_receipt: number | null;
  readonly fefo_policy: string;
  readonly allow_quarantine: boolean;
  readonly allow_serial_reuse: boolean;
  readonly lead_time_days: number | null;
}

interface TrackingProfilesResponse {
  readonly items: readonly TrackingProfileItem[];
  readonly total: number;
}

interface ProfileHistoryItem {
  readonly historyId: string;
  readonly productId: string;
  readonly version: number;
  readonly previousTrackingMode: string;
  readonly newTrackingMode: string;
  readonly changeReason: string;
  readonly changedBy: string;
  readonly createdAt: string;
}

const MODE_FILTER_OPTIONS = [
  { value: '', label: 'همه' },
  ...optionsOf(TRACKING_MODE_META, TRACKING_MODE_KEYS),
];
const PRECISION_STEPS = [0, 1, 2, 3, 4] as const;

export function TrackingProfilesPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { toasts, push, dismiss } = useToasts();

  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [editingItem, setEditingItem] = useState<TrackingProfileItem | null>(null);
  const [historyProduct, setHistoryProduct] = useState<TrackingProfileItem | null>(null);

  // Edit form
  const [formMode, setFormMode] = useState<string>('none');
  const [formPrecision, setFormPrecision] = useState<number>(0);
  const [formReqMfgDate, setFormReqMfgDate] = useState(false);
  const [formReqExpiry, setFormReqExpiry] = useState(false);
  const [formMinShelfLife, setFormMinShelfLife] = useState('');
  const [formFefoPolicy, setFormFefoPolicy] = useState('off');
  const [formAllowQuarantine, setFormAllowQuarantine] = useState(true);
  const [formAllowSerialReuse, setFormAllowSerialReuse] = useState(false);
  const [formAllowMigration, setFormAllowMigration] = useState(false);
  const [formReason, setFormReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim());
  const url = useMemo(() => {
    if (!slug) return null;
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (modeFilter) params.set('trackingMode', modeFilter);
    return `${base}/api/v1/inventory/tracking-profiles?${params.toString()}`;
  }, [base, debouncedSearch, modeFilter, slug]);

  const result = useApiGet<TrackingProfilesResponse>(url, [url]);
  const history = useApiGet<readonly ProfileHistoryItem[]>(
    slug && historyProduct
      ? `${base}/api/v1/inventory/products/${historyProduct.product_id}/tracking-profile/history`
      : null,
    [slug, historyProduct?.product_id],
  );

  const openEditModal = (item: TrackingProfileItem) => {
    setEditingItem(item);
    setFormMode(canonicalTrackingMode(item.tracking_mode));
    setFormPrecision(item.precision ?? 0);
    setFormReqMfgDate(Boolean(item.requires_manufacture_date));
    setFormReqExpiry(Boolean(item.requires_expiry));
    setFormMinShelfLife(
      item.min_shelf_life_days_receipt != null ? String(item.min_shelf_life_days_receipt) : '',
    );
    setFormFefoPolicy(item.fefo_policy ?? 'off');
    setFormAllowQuarantine(item.allow_quarantine ?? true);
    setFormAllowSerialReuse(Boolean(item.allow_serial_reuse));
    setFormAllowMigration(false);
    setFormReason('');
    setSaveError(null);
  };

  const handleSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingItem || saving) return;

    const shelfText = toEnglishDigits(formMinShelfLife).trim();
    if (shelfText !== '' && !/^\d+$/.test(shelfText)) {
      setSaveError('حداقل ماندگاری باید عدد صحیح (تعداد روز) باشد.');
      return;
    }
    if (formAllowMigration && formReason.trim() === '') {
      setSaveError('برای مهاجرت، دلیل تغییر یا شمارهٔ مصوبه را وارد کنید.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await apiPut(`${base}/api/v1/inventory/products/${editingItem.product_id}/tracking-profile`, {
        trackingMode: formMode,
        precision: formPrecision,
        requiresManufactureDate: formReqMfgDate,
        requiresExpiry: formReqExpiry,
        minShelfLifeDaysReceipt: shelfText ? Number(shelfText) : null,
        fefoPolicy: formFefoPolicy,
        allowQuarantine: formAllowQuarantine,
        allowSerialReuse: formAllowSerialReuse,
        allowMigrationWizard: formAllowMigration,
        changeReason: formReason.trim() || undefined,
      });
      setEditingItem(null);
      push('قرارداد ردیابی ذخیره شد.');
      result.reload();
    } catch (err: unknown) {
      setSaveError(bannerMessageFrom(err, 'ذخیرهٔ قرارداد ردیابی انجام نشد.'));
    } finally {
      setSaving(false);
    }
  };

  const columns: V2GridColumn<TrackingProfileItem>[] = [
    {
      key: 'code',
      header: 'کد کالا',
      render: (r) => <span className="inv-code ltr">{r.product_code}</span>,
    },
    {
      key: 'name',
      header: 'نام کالا',
      wrap: true,
      render: (r) => <span className="inv-cell__main">{r.product_name}</span>,
    },
    {
      key: 'uom',
      header: 'واحد پایه',
      muted: true,
      render: (r) => r.base_uom_name,
    },
    {
      key: 'mode',
      header: 'شیوهٔ ردیابی',
      render: (r) => {
        const meta = metaOf(TRACKING_MODE_META, r.tracking_mode);
        return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
      },
    },
    {
      key: 'precision',
      header: 'دقت اعشار',
      align: 'end',
      render: (r) => <span className="sk-num">{faDigits(r.precision ?? 0)} رقم</span>,
    },
    {
      key: 'fefo',
      header: 'سیاست FEFO',
      render: (r) => labelOf(FEFO_POLICY_LABEL, r.fefo_policy),
    },
    {
      key: 'shelf_life',
      header: 'حداقل ماندگاری ورود',
      align: 'end',
      render: (r) =>
        r.min_shelf_life_days_receipt != null ? (
          <span className="sk-num">{faDigits(r.min_shelf_life_days_receipt)} روز</span>
        ) : (
          <span>—</span>
        ),
    },
    {
      key: 'flags',
      header: 'الزام‌ها و اجازه‌ها',
      wrap: true,
      render: (r) => (
        <span className="inv-chips">
          {r.requires_manufacture_date ? (
            <V2Badge tone="neutral">تاریخ تولید الزامی</V2Badge>
          ) : null}
          {r.requires_expiry ? <V2Badge tone="neutral">تاریخ انقضا الزامی</V2Badge> : null}
          {r.allow_quarantine ? <V2Badge tone="neutral">قرنطینه‌پذیر</V2Badge> : null}
          {r.allow_serial_reuse ? <V2Badge tone="neutral">استفادهٔ مجدد سریال</V2Badge> : null}
        </span>
      ),
    },
    {
      key: 'version',
      header: 'نسخه',
      align: 'end',
      muted: true,
      render: (r) => <span className="sk-num">{faDigits(r.profile_version)}</span>,
    },
    {
      key: 'actions',
      header: 'عملیات',
      hideable: false,
      render: (r) => (
        <span className="inv-actions">
          <V2Button size="sm" variant="secondary" onClick={() => openEditModal(r)}>
            ویرایش قرارداد
          </V2Button>
          <V2Button size="sm" variant="ghost" onClick={() => setHistoryProduct(r)}>
            تاریخچه
          </V2Button>
        </span>
      ),
    },
  ];

  if (!slug) return null;

  const total = result.status === 'ready' ? result.data.total : 0;

  return (
    <div className="inv-page">
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            result.error.status === 403
              ? 'دسترسی مشاهدهٔ قراردادهای ردیابی را ندارید'
              : 'قراردادهای ردیابی قابل نمایش نیستند'
          }
        />
      ) : null}

      {result.status === 'ready' ? (
        <V2DataGrid
          icon="shield"
          title="قراردادهای ردیابی کالا"
          subtitle={`${faDigits(total)} کالا`}
          search={{ value: search, onChange: setSearch, placeholder: 'کد یا نام کالا' }}
          filters={[
            {
              id: 'mode',
              label: 'شیوهٔ ردیابی',
              value: modeFilter,
              options: MODE_FILTER_OPTIONS,
              onChange: setModeFilter,
            },
          ]}
          columns={columns}
          rows={result.data.items}
          rowKey={(r) => r.product_id}
          zebra
          storageKey="inventory.tracking-profiles"
          emptyState={
            <V2EmptyState
              icon="shield"
              title="کالایی پیدا نشد"
              description="کالایی با این جست‌وجو یا فیلتر موجود نیست."
              compact
            />
          }
        />
      ) : null}

      <V2Modal
        open={editingItem !== null}
        onClose={() => setEditingItem(null)}
        dismissible={!saving}
        size="lg"
        title={editingItem ? `قرارداد ردیابی: ${editingItem.product_name}` : 'قرارداد ردیابی'}
        footer={
          <>
            <V2Button variant="ghost" disabled={saving} onClick={() => setEditingItem(null)}>
              انصراف
            </V2Button>
            <V2Button
              type="submit"
              form="tracking-profile-form"
              variant="primary"
              disabled={saving}
            >
              {saving ? 'در حال ذخیره…' : 'ذخیرهٔ قرارداد'}
            </V2Button>
          </>
        }
      >
        <form
          id="tracking-profile-form"
          className="inv-form"
          noValidate
          onSubmit={handleSaveProfile}
        >
          {editingItem ? (
            <p className="inv-form__lead">
              کد <span className="ltr">{editingItem.product_code}</span> · نسخهٔ فعلی{' '}
              {faDigits(editingItem.profile_version)}
            </p>
          ) : null}
          {saveError ? <V2Alert tone="danger">{saveError}</V2Alert> : null}

          <V2FormField label="شیوهٔ ردیابی" width="full">
            <select
              className="sk-select"
              value={formMode}
              onChange={(e) => setFormMode(e.target.value)}
            >
              {optionsOf(TRACKING_MODE_META, TRACKING_MODE_KEYS).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </V2FormField>

          <div className="inv-form__row">
            <V2FormField label="دقت اعشار مجاز" width="full">
              <select
                className="sk-select"
                value={formPrecision}
                onChange={(e) => setFormPrecision(Number(e.target.value))}
              >
                {PRECISION_STEPS.map((p) => (
                  <option key={p} value={p}>
                    {p === 0 ? 'بدون اعشار (فقط عدد صحیح)' : `${faDigits(p)} رقم اعشار`}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="سیاست خروج FEFO" width="full">
              <select
                className="sk-select"
                value={formFefoPolicy}
                onChange={(e) => setFormFefoPolicy(e.target.value)}
              >
                {FEFO_POLICY_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {FEFO_POLICY_LABEL[key]}
                  </option>
                ))}
              </select>
            </V2FormField>
          </div>

          <V2FormField label="حداقل ماندگاری هنگام ورود (روز)" width="full" hint="اختیاری">
            <input
              className="sk-input ltr sk-num"
              inputMode="numeric"
              placeholder="مثلاً ۳۰"
              value={formMinShelfLife}
              onChange={(e) => setFormMinShelfLife(e.target.value)}
            />
          </V2FormField>

          <div className="inv-form__checks">
            <V2Checkbox
              label="تاریخ تولید الزامی است"
              checked={formReqMfgDate}
              onCheckedChange={setFormReqMfgDate}
            />
            <V2Checkbox
              label="تاریخ انقضا الزامی است"
              checked={formReqExpiry}
              onCheckedChange={setFormReqExpiry}
            />
            <V2Checkbox
              label="موجودی قرنطینه مجاز است"
              checked={formAllowQuarantine}
              onCheckedChange={setFormAllowQuarantine}
            />
            <V2Checkbox
              label="استفادهٔ مجدد از سریال مجاز است"
              checked={formAllowSerialReuse}
              onCheckedChange={setFormAllowSerialReuse}
            />
          </div>

          <V2Alert tone="warn" title="حفاظت از ردیابی تاریخی">
            <p className="inv-form__note">
              اگر این کالا گردش یا موجودی داشته باشد، تغییر ساختاری شیوهٔ ردیابی مسدود می‌شود؛ مگر
              اینکه مجوز مهاجرت و دلیل رسمی ثبت شود.
            </p>
            <V2Checkbox
              label="اجرای مهاجرت برای عبور از قفل گردش تاریخی"
              checked={formAllowMigration}
              onCheckedChange={setFormAllowMigration}
            />
            {formAllowMigration ? (
              <V2FormField label="دلیل تغییر یا شمارهٔ مصوبه" required width="full">
                <input
                  className="sk-input"
                  placeholder="مثلاً مصوبهٔ هیئت‌مدیره برای شروع کنترل سریال"
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                />
              </V2FormField>
            ) : null}
          </V2Alert>
        </form>
      </V2Modal>

      <V2Modal
        open={historyProduct !== null}
        onClose={() => setHistoryProduct(null)}
        size="lg"
        title={historyProduct ? `تاریخچهٔ قرارداد: ${historyProduct.product_name}` : 'تاریخچه'}
        footer={
          <V2Button variant="secondary" onClick={() => setHistoryProduct(null)}>
            بستن
          </V2Button>
        }
      >
        {historyProduct ? (
          <p className="inv-form__lead">
            کد <span className="ltr">{historyProduct.product_code}</span>
          </p>
        ) : null}
        {history.status === 'loading' ? <LoadingState /> : null}
        {history.status === 'error' ? (
          <StatePanel
            variant={history.error.status === 403 ? 'permission_denied' : 'network_error'}
            title={history.error.status === 403 ? 'دسترسی ندارید' : 'تاریخچه قابل نمایش نیست'}
          />
        ) : null}
        {history.status === 'ready' && history.data.length === 0 ? (
          <V2EmptyState
            icon="clock"
            title="تاریخچه‌ای ثبت نشده است"
            description="هنوز تغییری برای این قرارداد ثبت نشده است."
            compact
          />
        ) : null}
        {history.status === 'ready' && history.data.length > 0 ? (
          <ol className="inv-timeline">
            {history.data.map((h) => (
              <li key={h.historyId} className="inv-timeline__item">
                <div className="inv-timeline__head">
                  <strong>
                    نسخهٔ {faDigits(h.version)}:{' '}
                    {metaOf(TRACKING_MODE_META, h.previousTrackingMode).label}
                    {' ← '}
                    {metaOf(TRACKING_MODE_META, h.newTrackingMode).label}
                  </strong>
                  <span className="inv-timeline__when sk-num">
                    {displayJalaliDateTime(h.createdAt)}
                  </span>
                </div>
                {h.changeReason ? (
                  <div className="inv-timeline__sub">دلیل: {h.changeReason}</div>
                ) : null}
              </li>
            ))}
          </ol>
        ) : null}
      </V2Modal>

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
